import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react'
import moment from 'moment'
import {
  Box,
  Card,
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
  useTheme
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import _ from 'lodash'
import useOrgFilter from '../hooks/useOrgFilter'
import useMpciTableData from '../hooks/useMpciTableData'
import MpciScrollBar from './MpciScrollBar'

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    overflow: 'unset'
  },
  tableHead: {
    backgroundColor: theme.palette.background.sectionOddBg,
    '& .MuiTableCell-head': {
      fontSize: 12,
      fontWeight: 700,
      textTransform: 'uppercase',
      padding: '12px 16px'
    }
  },
  tableRow: {
    '& .MuiTableCell-root': {
      padding: '8px 16px',
      fontSize: theme.typography.body2.fontSize,
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      cursor: 'pointer'
    }
  },
  loader: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    left: 0,
    opacity: 0.8,
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: 10
  },
  toolbar: {
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  pagination: {
    padding: '12px 16px',
    borderTop: `1px solid ${theme.palette.divider}`
  },
  emptyState: {
    padding: theme.spacing(6, 2),
    textAlign: 'center'
  },
  searchField: {
    minWidth: 160
  },
  sortIndicator: {
    marginRight: 4
  },
  tableWrapper: {
    overflowX: 'scroll',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  bottomScrollBar: {
    position: 'sticky',
    bottom: 0
  },
}))

/**
 * MPCI-scoped table component.
 * Uses useMpciTableData internally — handles wrapped API response:
 *   { status, message, data: { content, totalElements, totalPages } }
 *
   * Props:
 *   url               {string}   - POST list endpoint
 *   columns           {Array}    - [{ field, title, sortKey?, render? }]
 *   filters           {Array}    - [{ name, label, type: 'text'|'select', options? }]
 *   actions           {Array}    - toolbar buttons [{ label, icon, onClick }]
 *   rowActions        {Array}    - row menu items [{ label, icon, onClick, disabled? }]
 *   onRowClick        {fn}       - row click handler
 *   defaultSort       {object}   - { field, direction: 'ASC'|'DESC' }
 *   flat              {boolean}  - no outer Card wrapper
 *   selectionConfig   {object}   - enables multiselect:
 *     isSelectable(row)  {fn}    - returns true if row can be selected
 *     disabledTooltip    {string}- tooltip shown on disabled checkbox
 *     onSelectionChange  {fn}    - (selectedRows) => void — called on every change
 */
const MpciTable = forwardRef((props, ref) => {
    const {
        url,
    columns = [],
    filters = [],
    actions = [],
    rowActions = [],
    onRowClick,
    defaultSort = { field: 'createdDate', direction: 'DESC' },
    flat = false,
    selectionConfig = null
  } = props

  // ─── Row Selection state ────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = React.useState(new Set())

  // Stable ref to avoid stale closure in selection callbacks
  const selectionChangeRef = useRef(null)
  selectionChangeRef.current = selectionConfig?.onSelectionChange ?? null

  const notifySelectionChange = (newSet, currentData) => {
    if (selectionChangeRef.current) {
      const selectedRows = currentData.filter((r) => newSet.has(r.id))
      selectionChangeRef.current(selectedRows)
    }
  }

  const prevDataRef = useRef([])

  const classes = useStyles()
  const theme = useTheme()

  // ─── Org filter (admin sees dropdown, others scoped to own orgId) ───────────
  const { isAdmin, orgList, selectedOrgId, setSelectedOrgId, effectiveOrgId } = useOrgFilter()

  const [sort, setSort] = useState(defaultSort)
    const [filterValues, setFilterValues] = useState(
    filters.reduce((acc, f) => {
      if (f.type === 'daterange') {
        return { ...acc, [`${f.name}Start`]: null, [`${f.name}End`]: null }
      }
      if (f.type === 'multiselect') {
        return { ...acc, [f.name]: [] }
      }
      return { ...acc, [f.name]: '' }
    }, {})
  )

    // Build flat request body matching BE contract:
  // { page, size, sortBy, sortDir, orgId?, <filterField>: value|null, ... }
    const buildParams = (page = 0, pageSize = 10, sortState = sort, filterState = filterValues, orgId = effectiveOrgId) => {
    const flatFilters = filters.reduce((acc, f) => {
      if (f.type === 'daterange') {
        const start = filterState[`${f.name}Start`]
        const end   = filterState[`${f.name}End`]
        acc[`${f.name}Start`] = start ? moment(start).startOf('day').format('YYYY-MM-DDTHH:mm:ss') : null
        acc[`${f.name}End`]   = end   ? moment(end).endOf('day').format('YYYY-MM-DDTHH:mm:ss')   : null
      } else if (f.type === 'multiselect') {
        const val = filterState[f.name]
        acc[f.name] = Array.isArray(val) && val.length > 0 ? val : null
      } else {
        const val = filterState[f.name]
        acc[f.name] = val !== '' && val != null ? val : null
      }
      return acc
    }, {})
        return {
      page,
      size: pageSize,
      sortBy: sortState.field,
      sortDir: sortState.direction,
      orgId: orgId ?? null,   // always present so updateParams merge correctly overrides prev value
      ...flatFilters
    }
  }

    const { data, totalElements, totalPages, loading, error, params, fetchPage, updateParams } =
    useMpciTableData(url, buildParams())

  // Reset selection when data page changes (filter/sort/refresh/page-turn)
  useEffect(() => {
    const prevIds = prevDataRef.current.map((r) => r.id).join(',')
    const nextIds = data.map((r) => r.id).join(',')
    if (prevIds !== nextIds) {
      prevDataRef.current = data
      setSelectedIds(new Set())
      if (selectionChangeRef.current) selectionChangeRef.current([])
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const tableContentRef = useRef(null)
  const topScrollBarRef = useRef(null)
  const bottomScrollBarRef = useRef(null)
  const tableContentWrapperRef = useRef(null)

    // Sync filterValues when filters prop changes (e.g. orgOptions loads async)
  // Only adds MISSING keys — does not reset existing user-typed values
  useEffect(() => {
    setFilterValues((prev) => {
      const next = { ...prev }
      let changed = false
      filters.forEach((f) => {
        if (f.type === 'daterange') {
          if (!((`${f.name}Start`) in next)) { next[`${f.name}Start`] = null; changed = true }
          if (!((`${f.name}End`)   in next)) { next[`${f.name}End`]   = null; changed = true }
        } else if (f.type === 'multiselect') {
          if (!(f.name in next)) { next[f.name] = []; changed = true }
        } else {
          if (!(f.name in next)) { next[f.name] = ''; changed = true }
        }
      })
      return changed ? next : prev
    })
  }, [filters]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch ONLY when effectiveOrgId changes after initial mount
  // (skip on mount — useMpciTableData already fires the first fetch via its own useEffect)
  const orgMountedRef = useRef(false)
  useEffect(() => {
    if (!orgMountedRef.current) {
      orgMountedRef.current = true
      return
    }
    updateParams({ ...buildParams(0, params.size ?? 10, sort, filterValues, effectiveOrgId) })
  }, [effectiveOrgId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Expose performRefresh + clearSelection to parent via forwardRef
  useImperativeHandle(ref, () => ({
    performRefresh: () => fetchPage(),
    clearSelection: () => {
      setSelectedIds(new Set())
      if (selectionChangeRef.current) selectionChangeRef.current([])
    }
  }), [fetchPage])

  const pageIndex = params.page ?? 0
  const pageSize = params.size ?? 10

  // ─── Sort ──────────────────────────────────────────────────────────────────
  function handleSort(sortKey) {
    if (!sortKey) return
    const newDir =
      sort.field === sortKey && sort.direction === 'ASC' ? 'DESC' : 'ASC'
    const newSort = { field: sortKey, direction: newDir }
    setSort(newSort)
    updateParams({ ...buildParams(0, pageSize, newSort, filterValues) })
  }

  // ─── Filter ────────────────────────────────────────────────────────────────
  const debounceRef = useRef(null)
    function handleFilterChange(name, value) {
    const newFilters = { ...filterValues, [name]: value }
    setFilterValues(newFilters)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      updateParams({ ...buildParams(0, pageSize, sort, newFilters) })
    }, 500)
  }

  function handleDateRangeChange(filterName, key, date) {
    // key: 'Start' | 'End'
    const fieldKey = `${filterName}${key}`
    const newFilters = { ...filterValues, [fieldKey]: date }
    setFilterValues(newFilters)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      updateParams({ ...buildParams(0, pageSize, sort, newFilters) })
    }, 500)
  }

  // ─── Pagination ────────────────────────────────────────────────────────────
  function handlePageChange(newPage) {
    updateParams({ page: newPage })
  }

  function handlePageSizeChange(e) {
    updateParams({ page: 0, size: Number(e.target.value) })
  }

  const start = totalElements === 0 ? 0 : pageIndex * pageSize + 1
  const end = Math.min(pageIndex * pageSize + pageSize, totalElements)

  // ─── Selection helpers ────────────────────────────────────────────────────
  const selectableRows = selectionConfig ? data.filter((r) => selectionConfig.isSelectable(r)) : []
  const allSelectableSelected = selectableRows.length > 0 && selectableRows.every((r) => selectedIds.has(r.id))
  const someSelected = selectableRows.some((r) => selectedIds.has(r.id))
  const isIndeterminate = someSelected && !allSelectableSelected

  function handleSelectAll(e) {
    const next = new Set(selectedIds)
    if (e.target.checked) {
      selectableRows.forEach((r) => next.add(r.id))
    } else {
      selectableRows.forEach((r) => next.delete(r.id))
    }
    setSelectedIds(next)
    notifySelectionChange(next, data)
  }

  function handleRowSelect(e, row) {
    e.stopPropagation()
    const next = new Set(selectedIds)
    if (next.has(row.id)) {
      next.delete(row.id)
    } else {
      next.add(row.id)
    }
    setSelectedIds(next)
    notifySelectionChange(next, data)
  }

      const tableContent = (
    <Box className={classes.root} data-testid='mpci-table'>
            {/* ── Toolbar ── */}
      <Box className={classes.toolbar}>
        <Grid container alignItems='center' spacing={1}>
          <Grid item xs>
            <Typography variant='caption'>
              {totalElements > 0 ? `${start}–${end} of ${totalElements}` : ''}
            </Typography>
          </Grid>

                                                            {/* Organisation autocomplete — admin only */}
          {isAdmin && (
            <Grid item>
              <Autocomplete
                options={orgList}
                getOptionLabel={(opt) => opt.name ?? ''}
                getOptionSelected={(opt, val) => opt.id === val.id}
                value={orgList.find((o) => o.id === selectedOrgId) ?? null}
                onChange={(_, newVal) => setSelectedOrgId(newVal ? newVal.id : null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Organisation'
                    variant='outlined'
                    size='small'
                    style={{ minWidth: 220 }}
                  />
                )}
                noOptionsText='No organisations found'
              />
            </Grid>
          )}

          <Grid item>
            <Tooltip title='Refresh' placement='top'>
              <IconButton size='small' onClick={() => fetchPage()}>
                <FontAwesomeIcon icon={['fal', 'refresh']} />
              </IconButton>
            </Tooltip>
          </Grid>
          {actions.map((action, i) => (
            <Grid key={i} item>
              <action.component />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Table ── */}
      <Box position='relative'>
        <MpciScrollBar
          tableContentRef={tableContentRef}
          ref={topScrollBarRef}
          onScroll={(e) => {
            if (bottomScrollBarRef.current) bottomScrollBarRef.current.scrollLeft = e.target.scrollLeft
            if (tableContentWrapperRef.current) tableContentWrapperRef.current.scrollLeft = e.target.scrollLeft
          }}
        />
        <div className={classes.tableWrapper} ref={tableContentWrapperRef}>
          <Table ref={tableContentRef}>
            {/* Header */}
            <TableHead className={classes.tableHead}>
              <TableRow>
                {/* Select-All checkbox header */}
                {selectionConfig && (
                  <TableCell padding='checkbox' style={{ width: 48 }}>
                    <Tooltip title='Select All'>
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={allSelectableSelected}
                        onChange={handleSelectAll}
                        size='small'
                        disabled={selectableRows.length === 0}
                      />
                    </Tooltip>
                  </TableCell>
                )}
              {columns.map((col, i) => {
                  const isSorted = sort.field === col.sortKey
                  return (
                    <TableCell
                      key={i}
                      onClick={() => handleSort(col.sortKey)}
                      style={{ cursor: col.sortKey ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
                    >
                      <Box display='flex' alignItems='center' style={{ gap: 6 }}>
                        {isSorted && (
                          <FontAwesomeIcon
                            icon={['fal', sort.direction === 'ASC' ? 'caret-up' : 'caret-down']}
                            color={theme.palette.primary.main}
                          />
                        )}
                        <Typography
                          variant='inherit'
                          color={isSorted ? 'primary' : 'initial'}
                        >
                          {col.title}
                        </Typography>
                      </Box>
                    </TableCell>
                  )
                })}
                {rowActions.length > 0 && (
                  <TableCell align='right'>Action</TableCell>
                )}
              </TableRow>

                            {/* Filter row */}
              {filters.length > 0 && (
                <TableRow>
                  {/* Empty placeholder for checkbox column */}
                  {selectionConfig && <TableCell padding='checkbox' />}
                  {columns.map((col, i) => {
                    const filter = filters.find((f) => f.name === col.field)
                    return (
                      <TableCell key={i} style={{ padding: '4px 8px' }}>
                        {filter ? (
                          filter.type === 'multiselect' ? (
                            <Select
                              multiple
                              value={filterValues[filter.name] ?? []}
                              onChange={(e) => handleFilterChange(filter.name, e.target.value)}
                              displayEmpty
                              size='small'
                              style={{ minWidth: 130, fontSize: 12, maxWidth: 220 }}
                              renderValue={(selected) => {
                                if (!selected || selected.length === 0) return <em style={{ color: '#aaa', fontSize: 12 }}>All</em>
                                const labels = selected.map((v) => {
                                  const opt = (filter.options ?? []).find((o) => o.value === v)
                                  return opt ? opt.label : v
                                })
                                if (labels.length <= 2) return <span style={{ fontSize: 12 }}>{labels.join(', ')}</span>
                                return <span style={{ fontSize: 12 }}>{labels[0]}, +{labels.length - 1} more</span>
                              }}
                            >
                              {(filter.options ?? []).map((opt) => (
                                <MenuItem key={opt.value} value={opt.value} style={{ fontSize: 12, padding: '4px 8px' }}>
                                  <Checkbox
                                    checked={(filterValues[filter.name] ?? []).indexOf(opt.value) > -1}
                                    size='small'
                                    style={{ padding: 4 }}
                                  />
                                  <ListItemText primary={opt.label} primaryTypographyProps={{ style: { fontSize: 12 } }} />
                                </MenuItem>
                              ))}
                            </Select>
                          ) : filter.type === 'select' ? (
                            <Select
                              value={filterValues[filter.name] ?? ''}
                              onChange={(e) => handleFilterChange(filter.name, e.target.value)}
                              displayEmpty
                              size='small'
                              style={{ minWidth: 100, fontSize: 12 }}
                            >
                              <MenuItem value=''>All</MenuItem>
                              {(filter.options ?? []).map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          ) : filter.type === 'daterange' ? (
                            <Box display='flex' alignItems='center' style={{ gap: 4 }}>
                              <TextField
                                type='date'
                                size='small'
                                variant='outlined'
                                value={filterValues[`${filter.name}Start`]
                                  ? moment(filterValues[`${filter.name}Start`]).format('YYYY-MM-DD')
                                  : ''}
                                onChange={(e) => handleDateRangeChange(filter.name, 'Start', e.target.value ? new Date(e.target.value) : null)}
                                inputProps={{ style: { fontSize: 11, padding: '4px 6px' } }}
                                style={{ width: 130 }}
                              />
                              <Typography variant='caption' color='textSecondary'>–</Typography>
                              <TextField
                                type='date'
                                size='small'
                                variant='outlined'
                                value={filterValues[`${filter.name}End`]
                                  ? moment(filterValues[`${filter.name}End`]).format('YYYY-MM-DD')
                                  : ''}
                                onChange={(e) => handleDateRangeChange(filter.name, 'End', e.target.value ? new Date(e.target.value) : null)}
                                inputProps={{ style: { fontSize: 11, padding: '4px 6px' } }}
                                style={{ width: 130 }}
                              />
                            </Box>
                          ) : (
                            <TextField
                              placeholder='Filter'
                              size='small'
                              value={filterValues[filter.name] ?? ''}
                              onChange={(e) => handleFilterChange(filter.name, e.target.value)}
                              inputProps={{ style: { fontSize: 12, padding: '4px 8px' } }}
                            />
                          )
                        ) : null}
                      </TableCell>
                    )
                  })}
                  {rowActions.length > 0 && <TableCell />}
                </TableRow>
              )}
            </TableHead>

            {/* Body */}
            <TableBody>
              {!loading && error ? (
                <TableRow>
                  <TableCell
                    align='center'
                    colSpan={columns.length + (rowActions.length > 0 ? 1 : 0)}
                    className={classes.emptyState}
                  >
                    <FontAwesomeIcon
                      icon={['fal', 'exclamation-triangle']}
                      style={{ fontSize: 40, color: theme.palette.error.main, marginBottom: 8 }}
                    />
                    <Typography variant='body2' color='error'>
                      Failed to load data
                    </Typography>
                    <Typography variant='caption' color='textSecondary'>
                      {error}
                    </Typography>
                    <Box mt={1}>
                      <IconButton size='small' onClick={() => fetchPage()} title='Retry'>
                        <FontAwesomeIcon icon={['fal', 'refresh']} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : !loading && data.length === 0 ? (
                <TableRow>
                  <TableCell
                    align='center'
                    colSpan={columns.length + (rowActions.length > 0 ? 1 : 0)}
                    className={classes.emptyState}
                  >
                    <FontAwesomeIcon
                      icon={['fal', 'inbox']}
                      style={{ fontSize: 40, color: theme.palette.text.disabled, marginBottom: 8 }}
                    />
                    <Typography variant='body2' color='textSecondary'>
                      No records found
                    </Typography>
                    <Typography variant='caption' color='textSecondary'>
                      Try adjusting your filters or create a new record.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, rowIdx) => {
                  const isSelectable = selectionConfig ? selectionConfig.isSelectable(row) : false
                  const isSelected = selectedIds.has(row.id)
                  return (
                    <TableRow
                      key={row.id ?? rowIdx}
                      className={classes.tableRow}
                      onClick={() => onRowClick && onRowClick(row)}
                      selected={isSelected}
                    >
                      {/* Per-row checkbox */}
                      {selectionConfig && (
                        <TableCell padding='checkbox' onClick={(e) => e.stopPropagation()}>
                          {isSelectable ? (
                            <Checkbox
                              checked={isSelected}
                              onChange={(e) => handleRowSelect(e, row)}
                              size='small'
                            />
                          ) : (
                            <Tooltip title={selectionConfig.disabledTooltip ?? 'Cannot select this record'}>
                              <span>
                                <Checkbox checked={false} disabled size='small' />
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                      {columns.map((col, colIdx) => (
                        <TableCell key={colIdx}>
                          {col.render ? col.render(row) : _.get(row, col.field) ?? '—'}
                        </TableCell>
                      ))}
                      {rowActions.length > 0 && (
                        <TableCell align='right' onClick={(e) => e.stopPropagation()}>
                          <RowActionsMenu rowActions={rowActions} row={row} />
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>

        {loading && (
          <Box className={classes.loader}>
            <CircularProgress size={32} />
          </Box>
        )}
        <MpciScrollBar
          className={classes.bottomScrollBar}
          tableContentRef={tableContentRef}
          ref={bottomScrollBarRef}
          onScroll={(e) => {
            if (topScrollBarRef.current) topScrollBarRef.current.scrollLeft = e.target.scrollLeft
            if (tableContentWrapperRef.current) tableContentWrapperRef.current.scrollLeft = e.target.scrollLeft
          }}
        />
      </Box>

      {/* ── Pagination ── */}
      <Box className={classes.pagination}>
        <Grid container alignItems='center' justify='flex-end' spacing={1}>
          <Grid item>
            <Typography variant='body2'>Rows per page:</Typography>
          </Grid>
          <Grid item>
            <Select value={pageSize} onChange={handlePageSizeChange} size='small'>
              {[10, 20, 50].map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item>
            <Typography variant='body2'>
              Page {totalPages === 0 ? 0 : pageIndex + 1} of {totalPages}
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title='First page'>
              <span>
                <IconButton
                  size='small'
                  onClick={() => handlePageChange(0)}
                  disabled={pageIndex === 0}
                >
                  <FontAwesomeIcon icon={['fal', 'angles-left']} />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title='Previous page'>
              <span>
                <IconButton
                  size='small'
                  onClick={() => handlePageChange(pageIndex - 1)}
                  disabled={pageIndex === 0}
                >
                  <FontAwesomeIcon icon={['fal', 'chevron-left']} />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title='Next page'>
              <span>
                <IconButton
                  size='small'
                  onClick={() => handlePageChange(pageIndex + 1)}
                  disabled={pageIndex >= totalPages - 1}
                >
                  <FontAwesomeIcon icon={['fal', 'chevron-right']} />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title='Last page'>
              <span>
                <IconButton
                  size='small'
                  onClick={() => handlePageChange(totalPages - 1)}
                  disabled={pageIndex >= totalPages - 1}
                >
                  <FontAwesomeIcon icon={['fal', 'angles-right']} />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>
            </Box>
  )

  return flat
    ? tableContent
    : <Card className={classes.root} variant='elevation'>{tableContent}</Card>
})

MpciTable.displayName = 'MpciTable'

// ─── Row Actions Menu ──────────────────────────────────────────────────────────
function RowActionsMenu({ rowActions, row }) {
  const [anchor, setAnchor] = useState(null)
  const theme = useTheme()

  const allHidden = rowActions.every((action) => action.hidden ? action.hidden(row) : false)

  if (allHidden) {
    return (
      <Typography variant='body2' style={{ color: 'inherit', textAlign: 'right', padding: '0 4px', userSelect: 'none' }}>
        &mdash;
      </Typography>
    )
  }

  return (
    <>
      <Tooltip title='Actions'>
        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation()
            setAnchor(e.currentTarget)
          }}
        >
          <FontAwesomeIcon icon={['fal', 'ellipsis-h']} />
        </IconButton>
      </Tooltip>
      {anchor && (
        <Paper
          elevation={8}
          style={{
            position: 'fixed',
            zIndex: 1300,
            top: anchor.getBoundingClientRect().bottom + 4,
            left: anchor.getBoundingClientRect().right - 160,
            minWidth: 160,
            borderRadius: 8
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box py={0.5}>
            {rowActions.map((action, i) => {
              const isHidden = action.hidden ? action.hidden(row) : false
              if (isHidden) return null
              const isDisabled = action.disabled ? action.disabled(row) : false
              return (
                <Box
                  key={i}
                  display='flex'
                  alignItems='center'
                  px={2}
                  py={1}
                  style={{
                    gap: 10,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.4 : 1
                  }}
                  onClick={() => {
                    if (!isDisabled) {
                      action.onClick(row)
                      setAnchor(null)
                    }
                  }}
                >
                  {action.icon && (
                    <Box style={{ width: 16, textAlign: 'center' }}>{action.icon}</Box>
                  )}
                  <Typography variant='body2' style={{ fontWeight: 600 }}>
                    {action.label}
                  </Typography>
                </Box>
              )
            })}
          </Box>
          {/* Backdrop to close */}
          <Box
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: -1
            }}
            onClick={() => setAnchor(null)}
          />
        </Paper>
      )}
    </>
  )
}

export default MpciTable